import { X } from '@phosphor-icons/react';
import clsx from 'clsx';
import React, { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { WEBSITE_OVERLAY_LAYERS } from '../../overlayLayers';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    closeLabel?: string;
    title: React.ReactNode;
    width?: string;
    variant?: 'default' | 'reference';
    animation?: 'zoom' | 'slideRight' | 'none';
    className?: string;
    overlayClassName?: string;
    contentClassName?: string;
    containerClassName?: string;
    headerClassName?: string;
    titleClassName?: string;
    bodyClassName?: string;
    closeButtonClassName?: string;
    cleanCloseButtonClassName?: string;
    showHeaderDivider?: boolean;
    hideHeader?: boolean;
    noPadding?: boolean;
    hideCloseButton?: boolean;
    contentScrollable?: boolean;
    closeOnOverlayClick?: boolean;
    zIndex?: number;
    children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    closeLabel = 'Close',
    title,
    width = 'max-w-md',
    variant = 'default',
    animation = 'zoom',
    className,
    overlayClassName,
    contentClassName,
    containerClassName,
    headerClassName,
    titleClassName,
    bodyClassName,
    closeButtonClassName,
    cleanCloseButtonClassName,
    showHeaderDivider = false,
    hideHeader = false,
    noPadding = false,
    hideCloseButton = false,
    contentScrollable = true,
    closeOnOverlayClick = true,
    zIndex = WEBSITE_OVERLAY_LAYERS.modal,
    children 
}) => {
    const titleId = useId();

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousOverflow || 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const isReferenceVariant = variant === 'reference';

    return createPortal(
        <div
            className={clsx(
                isReferenceVariant
                    ? [
                        'fixed inset-0 z-[10000] overflow-y-auto bg-black/40 backdrop-blur-sm [scrollbar-gutter:stable]',
                        animation !== 'none' && 'animate-in fade-in duration-200'
                    ]
                : animation === 'slideRight'
                    ? 'fixed inset-0 z-[10000] overflow-hidden bg-black/40 backdrop-blur-sm [scrollbar-gutter:stable]'
                    : 'fixed inset-0 z-[10000] overflow-y-auto bg-black/40 p-4 backdrop-blur-sm [scrollbar-gutter:stable]',
                animation === 'slideRight' && 'flex justify-end p-0',
                overlayClassName
            )}
            style={{ zIndex }}
            onClick={closeOnOverlayClick ? onClose : undefined}
        >
            <div className={clsx(
                'flex min-h-full items-center',
                animation === 'slideRight'
                    ? 'w-full justify-end'
                    : 'justify-center p-4'
            )}>
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={titleId}
                    className={clsx(
                        isReferenceVariant
                            ? 'relative flex w-full flex-col overflow-hidden rounded-[24px] border border-white/70 bg-white shadow-[0_24px_60px_rgba(26,22,47,0.18)]'
                            : 'relative flex w-full flex-col overflow-hidden rounded-[20px] border border-white/70 bg-white shadow-[0_18px_48px_rgba(26,22,47,0.14)]',
                        width,
                        className,
                        containerClassName,
                        animation === 'slideRight'
                            ? 'animate-in slide-in-from-right duration-300'
                            : isReferenceVariant && animation !== 'none'
                                ? 'animate-in zoom-in-95 duration-200'
                                : undefined
                    )}
                    onClick={(e) => e.stopPropagation()}
                >
                    {hideHeader ? (
                        <span id={titleId} className="sr-only">
                            {title}
                        </span>
                    ) : null}

                    {!hideHeader ? (
                        <div className={clsx(
                            isReferenceVariant
                                ? 'flex items-center justify-between border-b border-[#ECEFF5] px-6 pb-4 pt-5'
                                : 'flex items-center justify-between px-6 pb-4 pt-5',
                            !isReferenceVariant && showHeaderDivider && 'border-b border-[#ECEFF5]',
                            headerClassName
                        )}>
                            <h3 id={titleId} className={clsx(
                                'text-lg font-medium tracking-tight text-text-main',
                                titleClassName
                            )}>
                                {title}
                            </h3>
                            {!hideCloseButton ? (
                                <button
                                    type="button"
                                    onClick={onClose}
                                    aria-label={closeLabel}
                                    className={clsx(
                                        'rounded-xl p-1.5 text-text-sub transition-colors hover:bg-[#F4F5F8] hover:text-text-main',
                                        closeButtonClassName
                                    )}
                                >
                                    <X size={20} weight="regular" />
                                </button>
                            ) : null}
                        </div>
                    ) : !hideCloseButton ? (
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label={closeLabel}
                            className={clsx(
                                'absolute right-4 top-4 z-10 rounded-xl p-1.5 text-text-sub transition-colors hover:bg-[#F4F5F8] hover:text-text-main',
                                cleanCloseButtonClassName
                            )}
                        >
                            <X size={20} weight="regular" />
                        </button>
                    ) : null}

                    <div
                        className={clsx(
                            'min-h-0 flex-1',
                            contentScrollable ? 'overflow-y-auto [scrollbar-gutter:stable]' : 'overflow-hidden',
                            !noPadding && (isReferenceVariant ? 'px-6 pb-6 pt-4' : 'p-6'),
                            contentClassName,
                            bodyClassName
                        )}
                    >
                    {children}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default Modal;
